import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Download } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/shared/SEO';

declare global {
  interface Window {
    html2pdf?: any;
  }
}

// ATS data for logos
const atsLogos = [
  {
    name: "Greenhouse",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/greenhouse.svg"
  },
  {
    name: "Lever",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/lever.svg"
  },
  {
    name: "TeamTailor",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/teamtailor.svg"
  },
  {
    name: "Bullhorn",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/bullhorn.svg"
  },
  {
    name: "Workable",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/workable.svg"
  },
  {
    name: "Recruitee",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/recruitee.svg"
  },
  {
    name: "Breezy",
    logo: "https://d2mk0xd61wwcji.cloudfront.net/ui-auth-component/app_logos/short/breezy.svg"
  },
  {
    name: "Ashby",
    logo: "https://placehold.co/120x120/F8F7FF/9b87f5?text=Ashby"
  }
];

// Add at the top of the component
const html2pdfScriptUrl = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';

const SalesPitchDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesRef = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSlide]);

  // Scroll to top when slide changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [currentSlide]);

  // Load html2pdf.js dynamically if not present
  const loadHtml2Pdf = () => {
    if (!window.html2pdf) {
      const script = document.createElement('script');
      script.src = html2pdfScriptUrl;
      script.async = true;
      document.body.appendChild(script);
    }
  };

  useEffect(() => {
    loadHtml2Pdf();
  }, []);

  // PDF download handler
  const handleDownloadPDF = async () => {
    if (!window.html2pdf) {
      alert('PDF library is still loading. Please try again in a moment.');
      return;
    }
    if (pdfRef.current) {
      // Show the hidden PDF container
      pdfRef.current.style.display = 'block';
      // Use html2pdf to export
      await window.html2pdf()
        .set({
          margin: 0,
          filename: 'RoundOne-Sales-Pitch-Deck.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }
        })
        .from(pdfRef.current)
        .save();
      // Hide the PDF container again
      pdfRef.current.style.display = 'none';
    }
  };

  // Define slide content
  const slides = [
    // Slide 1: Title
    {
      title: "Transform Your Hiring Process",
      subtitle: "AI-Driven Candidate Screening",
      content: (
        <div className="flex flex-col items-center text-center">
          <img 
            src="/images/dashboard-screenshot.png" 
            alt="RoundOne AI Platform" 
            className="max-w-lg w-full mx-auto rounded-lg shadow-lg mb-8"
            onError={(e) => {
              e.currentTarget.src = 'https://placehold.co/800x500/F8F7FF/9b87f5?text=RoundOne+Dashboard';
            }}
          />
          <p className="text-xl text-gray-700 max-w-2xl">
            Say goodbye to CV clutter. Identify top talent with data-backed skill evaluations.
          </p>
        </div>
      )
    },
    
    // Slide 2: The Problem
    {
      title: "The Problem",
      subtitle: "Hiring Is Broken",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="bg-red-100 p-2 rounded-full mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Drowning in Applications</h3>
                  <p className="text-gray-600">Recruiters spend 70% of their time sifting through unqualified candidates</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-red-100 p-2 rounded-full mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Poor Candidate Experience</h3>
                  <p className="text-gray-600">Qualified candidates drop out due to lengthy, inefficient processes</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-red-100 p-2 rounded-full mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Biased Decision-Making</h3>
                  <p className="text-gray-600">Traditional screening relies on subjective evaluations and gut feelings</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-[#F8F7FF] to-[#E9E5FF] w-full max-w-md rounded-lg shadow-lg p-8 border border-[#E5DEFF]">
              <div className="flex justify-center items-center h-48 bg-white rounded-lg mb-6 p-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-[#D9D3FF] rounded-full w-full"></div>
                <div className="h-3 bg-[#D9D3FF] rounded-full w-5/6"></div>
                <div className="h-3 bg-[#D9D3FF] rounded-full w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Slide 3: The Solution
    {
      title: "Our Solution",
      subtitle: "RoundOne AI Candidate Assessment Platform",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="flex justify-center order-2 md:order-1">
            <div className="bg-gradient-to-br from-[#F8F7FF] to-[#E9E5FF] w-full max-w-md rounded-lg shadow-lg p-8 border border-[#E5DEFF]">
              <div className="flex justify-center items-center h-48 bg-white rounded-lg mb-6 p-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v6.5m0 0v6.5m0-6.5h8m-8 0H4" />
                  <path d="M12 22v-4" />
                  <path d="M20 10c.7 4.2-2 8-6 9" />
                  <path d="M4 10c-.7 4.2 2 8 6 9" />
                  <path d="M17 6c-1-1.5-3-2.5-5-2.5S8 4.5 7 6" />
                  <path d="M14 22h-4" />
                </svg>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-[#D9D3FF] rounded-full w-full"></div>
                <div className="h-3 bg-[#D9D3FF] rounded-full w-5/6"></div>
                <div className="h-3 bg-[#D9D3FF] rounded-full w-4/6"></div>
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">AI-Powered Screening</h3>
                  <p className="text-gray-600">Intelligent assessment of candidate skills and fit with detailed analytics</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Seamless Integration</h3>
                  <p className="text-gray-600">Works with your existing ATS for a frictionless workflow</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Data-Driven Decisions</h3>
                  <p className="text-gray-600">Objective candidate evaluation based on skills, not subjective impressions</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    
    // Slide 4: Key Features
    {
      title: "Key Features",
      subtitle: "Beyond Basic Screening Tools",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm hover:shadow-md hover:border-[#9b87f5] transition-all duration-300">
            <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path>
                <path d="M9 18h6"></path>
                <path d="M10 22h4"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Voice & Phone Screening</h3>
            <p className="text-gray-600">
              Candidates complete screenings by phone with our natural-sounding AI interviewer, saving you hours of calls.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm hover:shadow-md hover:border-[#9b87f5] transition-all duration-300">
            <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                <path d="M9 9h.01"></path>
                <path d="M15 9h.01"></path>
                <path d="M9 15h.01"></path>
                <path d="M15 15h.01"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Custom Questions</h3>
            <p className="text-gray-600">
              Create role-specific screening questions to evaluate exactly the skills that matter, cutting down bad-fit interviews.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm hover:shadow-md hover:border-[#9b87f5] transition-all duration-300">
            <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18"></path>
                <path d="m19 9-5 5-4-4-3 3"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Time-Saving Analytics</h3>
            <p className="text-gray-600">
              Get instant candidate rankings, skill scores, and AI-generated summaries that cut your decision time by 80%.
            </p>
          </div>
        </div>
      )
    },
    
    // ATS Integrations Slide
    {
      title: "Seamless ATS Integrations",
      subtitle: "Connect With Your Existing Tools",
      content: (
        <div className="flex flex-col items-center">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-8">
            {/* ATS platforms with logos from our data */}
            {atsLogos.map((ats, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border border-[#E5DEFF] hover:border-[#9b87f5] transition-all duration-300 text-center">
                <img 
                  src={ats.logo} 
                  alt={`${ats.name} logo`} 
                  className="w-16 h-16 object-contain mx-auto mb-3"
                  onError={(e) => {
                    e.currentTarget.src = `https://placehold.co/120x120/F8F7FF/9b87f5?text=${ats.name}`;
                  }}
                />
                <p className="text-sm font-medium">{ats.name}</p>
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            <div className="bg-white p-5 rounded-xl border border-[#E5DEFF] flex items-start gap-3">
              <div className="bg-[#F8F7FF] p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9H3m9 9a9 9 0 0 1-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Automated Data Sync</h3>
                <p className="text-sm text-gray-600">Candidate data flows automatically between platforms</p>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-xl border border-[#E5DEFF] flex items-start gap-3">
              <div className="bg-[#F8F7FF] p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m18 16 4-4-4-4" />
                  <path d="m6 8-4 4 4 4" />
                  <path d="m14.5 4-5 16" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Two-Click Setup</h3>
                <p className="text-sm text-gray-600">No coding required, integrate in minutes</p>
              </div>
            </div>
            
            <div className="bg-white p-5 rounded-xl border border-[#E5DEFF] flex items-start gap-3">
              <div className="bg-[#F8F7FF] p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Secure & Compliant</h3>
                <p className="text-sm text-gray-600">Enterprise-grade security for your data</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Slide 5: How It Works
    {
      title: "How It Works",
      subtitle: "Transform Your Screening Process in 4 Simple Steps",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
          <div className="relative h-full">
            <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
              <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                1
              </div>
              <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Create Assessments</h3>
              <p className="text-[#1A1F2C]/70 flex-grow">
                Select skills and build custom assessments tailored to your roles in minutes, not hours.
              </p>
            </div>
            
            <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          <div className="relative h-full">
            <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
              <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                2
              </div>
              <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Screen Candidates</h3>
              <p className="text-[#1A1F2C]/70 flex-grow">
                Send invites or let candidates complete phone screenings with our AI interviewer - cut admin time by 70%.
              </p>
            </div>
            
            <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          <div className="relative h-full">
            <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
              <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                3
              </div>
              <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Get Insights</h3>
              <p className="text-[#1A1F2C]/70 flex-grow">
                Receive detailed candidate evaluations with skill scores and AI-generated summaries.
              </p>
            </div>
            
            <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          <div className="h-full">
            <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
              <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                4
              </div>
              <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Hire Faster</h3>
              <p className="text-[#1A1F2C]/70 flex-grow">
                Make data-driven decisions and reduce time-to-hire by up to 60% with clear candidate rankings.
              </p>
            </div>
          </div>
        </div>
      )
    },
    
    // Slide 6: Testimonials
    {
      title: "Customer Success Stories",
      subtitle: "Why Teams Love RoundOne AI",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm hover:shadow-md hover:border-[#9b87f5] transition-all duration-300">
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map(star => (
                <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "RoundOne AI has completely transformed our technical screening process. We've reduced time-to-hire by 40% and improved quality of hires."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#9b87f5] flex items-center justify-center text-white font-bold">
                JD
              </div>
              <div>
                <p className="font-medium">Jamie Diaz</p>
                <p className="text-sm text-muted-foreground">Tech Recruiter, CloudTech</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm hover:shadow-md hover:border-[#9b87f5] transition-all duration-300">
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map(star => (
                <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "The phone assessment feature is a game-changer. Candidates love the flexibility and we get detailed insights without scheduling countless initial interviews."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#9b87f5] flex items-center justify-center text-white font-bold">
                SP
              </div>
              <div>
                <p className="font-medium">Sarah Parker</p>
                <p className="text-sm text-muted-foreground">HR Director, Nova Systems</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm hover:shadow-md hover:border-[#9b87f5] transition-all duration-300">
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map(star => (
                <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "The AI analysis gives us insights we couldn't get from traditional screening. It's like having an expert technical recruiter on the team 24/7."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#9b87f5] flex items-center justify-center text-white font-bold">
                RL
              </div>
              <div>
                <p className="font-medium">Ray Liang</p>
                <p className="text-sm text-muted-foreground">CTO, Bright Solutions</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Slide 7: Call To Action
    {
      title: "Ready to Transform Your Hiring?",
      subtitle: "Start for Free Today",
      content: (
        <div className="text-center">
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Join hundreds of forward-thinking companies already using RoundOne AI to make smarter hiring decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-10">
            <a href="/sign-in?tab=register" className="inline-block">
              <Button 
                className="gradient-button py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center" 
              >
                Get started for free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a href="/request-demo" className="inline-block">
              <Button 
                className="bg-white border border-[#9b87f5] text-roundone-purple py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center" 
              >
                Book a demo
              </Button>
            </a>
          </div>
          
          <div className="mt-10 bg-[#F8F7FF] p-6 rounded-xl border border-[#9b87f5]/20 inline-block">
            <p className="font-medium text-lg mb-2">Have questions? We're here to help.</p>
            <p className="text-gray-600 mb-4">Contact our team to discuss your specific needs</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact" className="text-roundone-purple font-medium hover:underline">
                Contact Us →
              </a>
              <div className="h-4 border-r border-gray-300 hidden sm:block"></div>
              <a href="mailto:mayur@roundone.ai" className="text-roundone-purple font-medium hover:underline flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-roundone-purple">
                  <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                </svg>
                mayur@roundone.ai
              </a>
            </div>
          </div>
        </div>
      )
    }
  ];
  
  // Structure for structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "RoundOne AI - Sales Pitch Deck",
    "description": "Transform your hiring process with AI-powered candidate screening. Learn how RoundOne AI can help you make better hiring decisions.",
    "url": "https://roundone.ai/sales-pitch-deck"
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F7FF]">
      <SEO 
        title="RoundOne AI - Sales Pitch Deck"
        description="Transform your hiring process with AI-powered candidate screening. Learn how RoundOne AI can help you make better hiring decisions."
        schema={[structuredData]}
      />
      
      {/* Add CSS animation */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .slide-animation {
          animation: fadeIn 0.6s ease-in-out;
        }
        .pdf-slide h2 { color: #5E4BD2; }
      `}} />
      
      <Header />
      {/* Download as PDF button */}
      <div className="container mx-auto px-4 flex justify-end mt-6">
        <Button onClick={handleDownloadPDF} variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Download as PDF
        </Button>
      </div>
      
      {/* Hidden PDF container for all slides */}
      <div ref={pdfRef} style={{ display: 'none' }}>
        <div style={{ margin: '0 auto' }}>
          {slides.map((slide, index) => (
            <div
              key={index}
              style={{
                pageBreakBefore: index === 0 ? 'auto' : 'always',
                width: '1120px',
                height: '793px',
                background: '#F8F7FF',
                padding: '32px 32px 0 32px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start'
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <p style={{ textTransform: 'uppercase', fontSize: 14, fontWeight: 500, letterSpacing: 1.5, color: '#9b87f5', marginBottom: 8 }}>{slide.subtitle}</p>
                <h2 style={{ fontSize: 32, fontWeight: 700, color: '#5E4BD2', marginBottom: 24 }}>{slide.title}</h2>
              </div>
              <div style={{ flex: 1 }}>{slide.content}</div>
              <div style={{ marginTop: 16, textAlign: 'center', fontSize: 12, color: '#999' }}>Page {index + 1} of {slides.length}</div>
            </div>
          ))}
        </div>
      </div>
      
      <div ref={containerRef} className="flex-grow py-12 md:py-16">
        <div className="container mx-auto px-4 relative">
          
          {/* Left arrow navigation (previous) */}
          <button
            className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 shadow-md flex items-center justify-center transition-all hover:bg-white ${
              currentSlide === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
            }`}
            onClick={handlePrev}
            disabled={currentSlide === 0}
            aria-label="Previous slide"
          >
            <ArrowLeft className="h-5 w-5 text-roundone-purple" />
          </button>
          
          {/* Right arrow navigation (next) */}
          <button
            className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 shadow-md flex items-center justify-center transition-all hover:bg-white ${
              currentSlide === slides.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
            }`}
            onClick={handleNext}
            disabled={currentSlide === slides.length - 1}
            aria-label="Next slide"
          >
            <ArrowRight className="h-5 w-5 text-roundone-purple" />
          </button>
          
          {/* Slides */}
          <div>
            {slides.map((slide, index) => (
              <div 
                key={index} 
                ref={el => slidesRef.current[index] = el}
                className={`slide-container py-12 ${index === currentSlide ? 'block slide-animation' : 'hidden'}`}
                id={`slide-${index}`}
              >
                <div className="max-w-6xl mx-auto">
                  <div className="text-center mb-12">
                    <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">
                      {slide.subtitle}
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold mb-8 text-roundone-dark-purple">
                      {slide.title}
                    </h2>
                  </div>
                  
                  <div className="slide-content">
                    {slide.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Slide indicator dots */}
          <div className="flex flex-col items-center gap-3 mt-12 mb-6">
            {/* Page number */}
            <div className="text-sm font-medium text-roundone-purple bg-white border border-[#9b87f5]/20 px-5 py-1.5 rounded-full shadow-sm mb-2">
              {currentSlide + 1} / {slides.length}
            </div>
            
            {/* Dots */}
            <div className="flex justify-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`h-2.5 rounded-full transition-all ${
                    currentSlide === index ? 'bg-[#9b87f5] w-8' : 'bg-[#E5DEFF] w-2.5'
                  }`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SalesPitchDeck; 